import { useEffect, useMemo, useState } from 'react'
import { Checkbox, Loader, SegmentedControl, Select, TextInput } from '@mantine/core'
import dayjs from '@/lib/dayjs'

import ScrollableScreen from '@/components/ui/ScrollableScreen'
import DocumentTitle from '@/components/ui/DocumentTitle'
import RouterLink from '@/components/ui/RouterLink'
import { useAxiosApi } from '@/hooks/useAxiosApi'
import { StatTiles, StatTile, compact } from '@/components/support/ai/StatTiles'
import {
    STATES,
    StateSwatch,
    StatesLegend,
    TodayBar,
    Ribbon,
    CompositionChart,
    IssuesChart,
    DailyTable,
} from '@/components/shopify/AvailabilityViz'

/**
 * B2C availability: what a customer could order on the site, day by day.
 * Reached by URL only (no sidebar entry). Everything below is derived in
 * memory from one API payload — ~100 products, nothing to paginate.
 */

const WINDOWS = [
    { value: '30', label: '30 jours' },
    { value: '60', label: '60 jours' },
    { value: '400', label: 'Depuis le 29/07' },
]

const SORTS = [
    { value: 'sales', label: 'Ventes 12 mois' },
    { value: 'unavailable', label: 'Temps non commandable' },
    { value: 'preorder', label: 'Temps en précommande' },
    { value: 'name', label: 'Nom' },
]

const STATE_INDEX = Object.fromEntries(STATES.map((s, i) => [s.key, i]))

const pctOf = (n, total) => (total ? Math.round((100 * n) / total) : 0)
const daysSince = (date) => dayjs().startOf('day').diff(dayjs(date), 'day')
const plural = (n, one, many = `${one}s`) => `${n} ${n > 1 ? many : one}`

/**
 * Per-product figures the page reads everywhere: today's counts from the
 * current variant states, time shares from the daily ribbon (days out of
 * scope excluded from the denominator).
 */
const enrich = (p) => {
    const today = [0, 0, 0, 0]
    p.variants.forEach((v) => today[STATE_INDEX[v.state]]++)
    const tracked = p.variants.length

    let variantDays = 0
    let unavailableDays = 0
    let preorderDays = 0
    let hadIssue = false
    p.daily.forEach((c) => {
        if (!c) return
        const total = c[0] + c[1] + c[2] + c[3]
        variantDays += total
        unavailableDays += c[3]
        preorderDays += c[1] + c[2]
        if (c[1] + c[2] + c[3] > 0) hadIssue = true
    })

    const overdue = p.variants.filter((v) => v.state === 'overdue')
    const allUnavailable = tracked > 0 && today[3] === tracked

    return {
        ...p,
        today,
        tracked,
        allUnavailable,
        // The product has been out since its last variant fell.
        outSinceDays: allUnavailable ? Math.min(...p.variants.map((v) => daysSince(v.since))) : null,
        maxOverdueDays: overdue.length ? Math.max(...overdue.map((v) => v.overdue_days)) : null,
        overdueCount: overdue.length,
        unavailableShare: variantDays ? unavailableDays / variantDays : 0,
        preorderShare: variantDays ? preorderDays / variantDays : 0,
        hadIssue,
    }
}

/* ------------------------------------------------------------- small parts */

const Card = ({ title, aside, children, className = '' }) => (
    <div className={`rounded-xl border border-gray-200/70 bg-white ${className}`}>
        {(title || aside) && (
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-5 pt-4">
                {title && <span className="text-sm font-medium text-gray-900">{title}</span>}
                {aside}
            </div>
        )}
        <div className="px-5 pb-4 pt-3">{children}</div>
    </div>
)

/** A quiet text tag: no fill, no border, just muted ink. */
const Tag = ({ tone = 'gray', children }) => (
    <span className={`text-[11px] ${tone === 'red' ? 'text-red-600' : tone === 'amber' ? 'text-amber-700' : 'text-gray-400'}`}>
        {children}
    </span>
)

const ProductLink = ({ product, className = '' }) => (
    <RouterLink
        href={`/products/shopify/${product.id}`}
        className={`font-medium text-gray-900 no-underline hover:text-blue-600 ${className}`}
    >
        {product.title}
    </RouterLink>
)

const AlertList = ({ title, hint, rows, empty, columns }) => (
    <Card title={title} aside={<span className="text-xs text-gray-400">{hint}</span>}>
        {rows.length === 0 ? (
            <p className="text-sm text-gray-400">{empty}</p>
        ) : (
            <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-400">
                    <tr>
                        {columns.map((c) => (
                            <th key={c.label} className={`pb-2 font-normal ${c.right ? 'text-right' : ''}`}>
                                {c.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((p) => (
                        <tr key={p.id} className="border-t border-gray-100">
                            {columns.map((c) => (
                                <td
                                    key={c.label}
                                    className={`py-2 ${c.right ? 'text-right tabular-nums text-gray-500' : 'pr-3'}`}
                                >
                                    {c.render(p)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        )}
    </Card>
)

/* ------------------------------------------------------------ the notebook */

const ROW_GRID = 'minmax(240px, 1.1fr) 132px minmax(280px, 2fr) 64px 64px'

const ProductRow = ({ product, days }) => (
    <div
        className="grid items-center gap-6 border-t border-gray-100 px-5 py-3"
        style={{ gridTemplateColumns: ROW_GRID }}
    >
        <div className="min-w-0">
            <div className="truncate text-sm">
                <ProductLink product={product} />
            </div>
            <div className="mt-0.5 flex flex-wrap gap-x-2.5 text-[11px] text-gray-400">
                <span>{plural(product.tracked, 'variante')}</span>
                {product.overdueCount > 0 && <Tag tone="amber">date précmd. dépassée</Tag>}
                {product.sales.d90 === 0 && <Tag tone="red">aucune vente 90 j</Tag>}
            </div>
        </div>
        <TodayBar today={product.today} />
        <Ribbon days={days} daily={product.daily} />
        <div className="text-right text-sm tabular-nums text-gray-500">{compact(product.sales.period)}</div>
        <div className="text-right text-sm tabular-nums text-gray-500">{compact(product.sales.d365)}</div>
    </div>
)

const RangeSection = ({ range, days }) => {
    const variants = range.products.reduce((n, p) => n + p.tracked, 0)
    const available = range.products.reduce((n, p) => n + p.today[0], 0)
    const overdue = range.products.reduce((n, p) => n + p.overdueCount, 0)

    return (
        <section className="overflow-hidden rounded-xl border border-gray-200/70 bg-white">
            <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 px-5 pb-3 pt-4">
                <span className="text-base font-semibold text-gray-900">{range.name}</span>
                <span className="text-xs text-gray-400">
                    {plural(range.products.length, 'produit')} · {plural(variants, 'variante')}
                </span>
                <span className="text-xs text-gray-500">{pctOf(available, variants)} % en stock</span>
                {overdue > 0 && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-700">
                        <StateSwatch state={STATES[2]} className="h-2 w-2" />
                        {plural(overdue, 'promesse dépassée', 'promesses dépassées')}
                    </span>
                )}
                <span className="ml-auto text-xs tabular-nums text-gray-400">{compact(range.sales)} u. sur 12 mois</span>
            </div>
            <div
                className="grid gap-6 px-5 pb-2 text-[11px] uppercase tracking-wide text-gray-400"
                style={{ gridTemplateColumns: ROW_GRID }}
            >
                <span>Produit</span>
                <span>Aujourd'hui</span>
                <span>
                    {dayjs(days[0]).format('D MMM')} → {dayjs(days[days.length - 1]).format('D MMM')}
                </span>
                <span className="text-right">Période</span>
                <span className="text-right">12 mois</span>
            </div>
            {range.products.map((p) => (
                <ProductRow key={p.id} product={p} days={days} />
            ))}
        </section>
    )
}

/* -------------------------------------------------------------------- page */

export default function ProductsAvailabilityPage() {
    const { requestGet } = useAxiosApi()
    const [windowDays, setWindowDays] = useState('60')
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)

    const [rangeFilter, setRangeFilter] = useState(null)
    const [search, setSearch] = useState('')
    const [sort, setSort] = useState('sales')
    const [issuesOnly, setIssuesOnly] = useState(false)

    useEffect(() => {
        let active = true
        setLoading(true)
        requestGet('/catalog/availability', { days: windowDays })
            .then(({ data }) => active && setData(data))
            .catch(() => active && setData(null))
            .finally(() => active && setLoading(false))
        return () => {
            active = false
        }
    }, [windowDays])

    const days = data?.period.days ?? []

    const products = useMemo(() => (data?.products ?? []).map(enrich), [data])

    // Live = tracked and not discontinued. Discontinued products are kept
    // aside; products with no tracked variant are out of scope altogether.
    const live = useMemo(() => products.filter((p) => p.tracked > 0 && !p.discontinued), [products])
    const discontinued = useMemo(() => products.filter((p) => p.discontinued), [products])

    // Day-by-day totals over the live catalogue, one series per state.
    const { series, totals } = useMemo(() => {
        const series = STATES.map(() => days.map(() => 0))
        const totals = days.map(() => 0)
        live.forEach((p) =>
            p.daily.forEach((c, j) => {
                if (!c) return
                c.forEach((n, i) => (series[i][j] += n))
                totals[j] += c[0] + c[1] + c[2] + c[3]
            })
        )
        return { series, totals }
    }, [live, days])

    const kpi = useMemo(() => {
        const today = [0, 0, 0, 0]
        live.forEach((p) => p.today.forEach((n, i) => (today[i] += n)))
        const total = today.reduce((a, b) => a + b, 0)
        return {
            total,
            today,
            outProducts: live.filter((p) => p.allUnavailable).length,
            overdueProducts: live.filter((p) => p.overdueCount > 0).length,
        }
    }, [live])

    const outages = useMemo(
        () => live.filter((p) => p.allUnavailable).sort((a, b) => b.sales.d365 - a.sales.d365),
        [live]
    )
    const overdues = useMemo(
        () => live.filter((p) => p.overdueCount > 0).sort((a, b) => b.maxOverdueDays - a.maxOverdueDays),
        [live]
    )

    const rangeOptions = useMemo(
        () => [...new Set(live.map((p) => p.range))].sort((a, b) => a.localeCompare(b, 'fr')),
        [live]
    )

    const ranges = useMemo(() => {
        const needle = search.trim().toLowerCase()
        const compare = {
            sales: (a, b) => b.sales.d365 - a.sales.d365,
            unavailable: (a, b) => b.unavailableShare - a.unavailableShare,
            preorder: (a, b) => b.preorderShare - a.preorderShare,
            name: (a, b) => a.title.localeCompare(b.title, 'fr'),
        }[sort]

        const filtered = live.filter(
            (p) =>
                (!rangeFilter || p.range === rangeFilter) &&
                (!needle || p.title.toLowerCase().includes(needle)) &&
                (!issuesOnly || p.hadIssue)
        )

        const byRange = new Map()
        filtered.forEach((p) => {
            if (!byRange.has(p.range)) byRange.set(p.range, { name: p.range, products: [], sales: 0 })
            const r = byRange.get(p.range)
            r.products.push(p)
            r.sales += p.sales.d365
        })

        return [...byRange.values()]
            .map((r) => ({ ...r, products: [...r.products].sort(compare) }))
            .sort((a, b) => (sort === 'name' ? a.name.localeCompare(b.name, 'fr') : b.sales - a.sales))
    }, [live, rangeFilter, search, sort, issuesOnly])

    const breadcrumbs = [{ label: 'Produits' }, { label: 'Disponibilité B2C' }]
    const rightSection = <SegmentedControl size="xs" value={windowDays} onChange={setWindowDays} data={WINDOWS} />

    return (
        <ScrollableScreen breadcrumbs={breadcrumbs} rightSection={rightSection}>
            <DocumentTitle title="BO - Disponibilité B2C - Baltic Watches" />

            {loading ? (
                <div className="flex justify-center py-20">
                    <Loader />
                </div>
            ) : !data ? (
                <p className="px-6 py-10 text-gray-400">Données indisponibles.</p>
            ) : (
                <main className="@container w-full">
                    <div className="mx-auto max-w-6xl space-y-10 px-6 py-8">
                        {/* 6.1 — header */}
                        <header className="flex flex-col gap-1">
                            <h1 className="text-xl font-semibold tracking-tight text-gray-900">
                                Ce qu'un client pouvait commander
                            </h1>
                            <p className="text-sm text-gray-500">
                                Du {dayjs(data.period.start).format('D MMMM')} au {dayjs(data.period.end).format('D MMMM YYYY')}, état
                                relevé chaque jour à midi · {plural(data.tracked.variants, 'variante suivie', 'variantes suivies')} sur{' '}
                                {plural(data.tracked.products, 'produit')} · dernier événement{' '}
                                {data.last_event_at ? dayjs(data.last_event_at).format('D MMM à HH:mm') : 'inconnu'}
                            </p>
                            {data.stale && (
                                <p className="mt-2 rounded-lg bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
                                    Aucun changement journalisé depuis plus de 24 h. Le journal n'écrit que les changements, mais un
                                    silence aussi long mérite de vérifier que la commande de suivi tourne toujours.
                                </p>
                            )}
                        </header>

                        {/* 6.2 — four KPIs, discontinued excluded */}
                        <StatTiles>
                            <StatTile
                                label="En stock"
                                value={pctOf(kpi.today[0], kpi.total)}
                                unit="%"
                                note={`${kpi.today[0]} variantes sur ${kpi.total}`}
                                points={days.map((d, j) => pctOf(series[0][j], totals[j]))}
                            />
                            <StatTile
                                label="En précommande"
                                value={pctOf(kpi.today[1] + kpi.today[2], kpi.total)}
                                unit="%"
                                note={`${kpi.today[1] + kpi.today[2]} variantes, dont ${kpi.today[2]} à date dépassée`}
                                points={days.map((d, j) => pctOf(series[1][j] + series[2][j], totals[j]))}
                            />
                            <StatTile
                                label="Non commandable"
                                value={pctOf(kpi.today[3], kpi.total)}
                                unit="%"
                                note={`${kpi.today[3]} variantes · ${plural(kpi.outProducts, 'produit')} sans aucune variante commandable`}
                                points={days.map((d, j) => pctOf(series[3][j], totals[j]))}
                            />
                            <StatTile
                                label="Promesses dépassées"
                                value={kpi.today[2]}
                                note={`variantes en précommande à date passée · ${plural(kpi.overdueProducts, 'produit concerné', 'produits concernés')}`}
                                points={series[2]}
                            />
                        </StatTiles>

                        {/* 6.3 — two charts, same date axis */}
                        <section className="flex flex-col gap-4">
                            <Card title="Composition du catalogue" aside={<StatesLegend />}>
                                <CompositionChart days={days} series={series} totals={totals} />
                                <p className="mt-3 text-xs text-gray-400">
                                    En part des variantes suivies : le périmètre grandit avec le catalogue, seules les parts se comparent
                                    dans le temps.
                                </p>
                            </Card>
                            <Card title="Les états à corriger" aside={<StatesLegend states={[STATES[3], STATES[2]]} />}>
                                <IssuesChart days={days} series={series} totals={totals} />
                            </Card>
                            <details>
                                <summary className="cursor-pointer select-none text-sm text-gray-500 hover:text-gray-900">
                                    Voir les chiffres jour par jour
                                </summary>
                                <div className="mt-3">
                                    <DailyTable days={days} series={series} totals={totals} />
                                </div>
                            </details>
                        </section>

                        {/* 6.4 — the two alert lists */}
                        <section className="grid gap-4 @3xl:grid-cols-2">
                            <AlertList
                                title="Ruptures en cours"
                                hint="par ventes 12 mois"
                                empty="Aucun produit entièrement non commandable aujourd'hui."
                                rows={outages}
                                columns={[
                                    { label: 'Produit', render: (p) => <ProductLink product={p} /> },
                                    { label: 'Variantes', right: true, render: (p) => `${p.today[3]}/${p.tracked}` },
                                    { label: 'Depuis', right: true, render: (p) => plural(p.outSinceDays, 'jour') },
                                    { label: '12 mois', right: true, render: (p) => compact(p.sales.d365) },
                                ]}
                            />
                            <AlertList
                                title="Précommandes échues"
                                hint="par retard sur la date annoncée"
                                empty="Aucune précommande à date dépassée."
                                rows={overdues}
                                columns={[
                                    { label: 'Produit', render: (p) => <ProductLink product={p} /> },
                                    { label: 'Variantes', right: true, render: (p) => `${p.overdueCount}/${p.tracked}` },
                                    { label: 'Retard', right: true, render: (p) => plural(p.maxOverdueDays, 'jour') },
                                    { label: '90 jours', right: true, render: (p) => compact(p.sales.d90) },
                                ]}
                            />
                        </section>

                        {/* 6.5 / 6.6 — the notebook, by range, with filters */}
                        <section className="flex flex-col gap-4">
                            <div className="flex flex-wrap items-end gap-3">
                                <Select
                                    size="sm"
                                    placeholder="Toutes les gammes"
                                    data={rangeOptions}
                                    value={rangeFilter}
                                    onChange={setRangeFilter}
                                    clearable
                                    searchable
                                    w={220}
                                />
                                <TextInput
                                    size="sm"
                                    placeholder="Rechercher un produit"
                                    value={search}
                                    onChange={(e) => setSearch(e.currentTarget.value)}
                                    w={240}
                                />
                                <Select size="sm" data={SORTS} value={sort} onChange={setSort} w={220} />
                                <Checkbox
                                    size="sm"
                                    label="Ruptures ou précommandes seulement"
                                    checked={issuesOnly}
                                    onChange={(e) => setIssuesOnly(e.currentTarget.checked)}
                                    className="pb-2"
                                />
                                <StatesLegend className="ml-auto" />
                            </div>

                            {ranges.length === 0 ? (
                                <p className="py-8 text-center text-sm text-gray-400">Aucun produit ne correspond.</p>
                            ) : (
                                ranges.map((r) => <RangeSection key={r.name} range={r} days={days} />)
                            )}
                        </section>

                        {/* 5 — discontinued products, kept readable but out of every figure */}
                        {discontinued.length > 0 && (
                            <details className="rounded-xl border border-dashed border-gray-200">
                                <summary className="cursor-pointer select-none px-5 py-4 text-sm text-gray-500 hover:text-gray-900">
                                    Fins de cycle · {plural(discontinued.length, 'produit')} exclus des indicateurs et des alertes
                                </summary>
                                <div className="pb-1">
                                    {discontinued.map((p) => (
                                        <ProductRow key={p.id} product={p} days={days} />
                                    ))}
                                </div>
                            </details>
                        )}
                    </div>
                </main>
            )}
        </ScrollableScreen>
    )
}
